import { useState, useCallback } from "react";
import { C } from "./utils";
import { GalleryTile } from "./Dashboard";

const ACT_DEACT_API = "https://cip.medsphere.net/api/act_deact_multiple";

// ─── Confirm dialog ───────────────────────────────────────────────────────────
export function ConfirmDialog({ confirm, onConfirm, onCancel }) {
  if (!confirm) return null;
  const isDelete = confirm.stateid === 4;
  return (
    <>
      <div onMouseDown={onCancel}
        style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.4)",
          zIndex:2000, cursor:"pointer" }} />
      <div onMouseDown={e => e.stopPropagation()}
        style={{ position:"fixed", top:"50%", left:"50%",
          transform:"translate(-50%,-50%)",
          width:"min(420px,88vw)", background:"#fff", borderRadius:12,
          boxShadow:"0 20px 60px rgba(0,0,0,0.22)", zIndex:2001,
          overflow:"hidden" }}>

        {/* header */}
        <div style={{ padding:"18px 20px 0", display:"flex", gap:12, alignItems:"flex-start" }}>
          <div style={{ width:40, height:40, borderRadius:10, flexShrink:0,
            display:"flex", alignItems:"center", justifyContent:"center", fontSize:20,
            background: isDelete ? "#FEF2F2" : confirm.stateid===2 ? "#F0FDF4" : "#FFFBEB" }}>
            {isDelete ? "🗑" : confirm.stateid===2 ? "✓" : "⊘"}
          </div>
          <div>
            <div style={{ fontSize:15, fontWeight:700, color:C.text }}>
              {confirm.stateid===2 ? "Activate Documents"
                : confirm.stateid===1 ? "Deactivate Documents"
                : "Delete Documents"}
            </div>
            <div style={{ fontSize:13, color:C.muted, marginTop:4, lineHeight:1.5 }}>
              {isDelete
                ? `Are you sure you want to permanently delete ${confirm.count} document${confirm.count!==1?"s":""}? This cannot be undone.`
                : `${confirm.stateid===2?"Activate":"Deactivate"} ${confirm.count} selected document${confirm.count!==1?"s":""}?`}
            </div>
          </div>
        </div>

        {/* footer */}
        <div style={{ padding:"16px 20px", display:"flex", gap:8, justifyContent:"flex-end" }}>
          <button onClick={onCancel}
            style={{ padding:"7px 18px", borderRadius:7,
              border:`1px solid ${C.border}`, background:"#F7F9FC",
              color:C.muted, fontSize:13, cursor:"pointer", fontWeight:500 }}>
            Cancel
          </button>
          <button onClick={onConfirm}
            style={{ padding:"7px 20px", borderRadius:7, border:"none",
              background: isDelete ? "#EF4444" : confirm.stateid===2 ? "#22C55E" : "#F59E0B",
              color:"#fff", fontSize:13, fontWeight:600, cursor:"pointer" }}>
            {isDelete ? "Delete" : confirm.stateid===2 ? "Activate" : "Deactivate"}
          </button>
        </div>
      </div>
    </>
  );
}

// ─── Bulk action bar ──────────────────────────────────────────────────────────
export function BulkActionBar({ selected, total, onSelectAll, onClearAll, onAction, loading, color }) {
  const count = selected.length;
  return (
    <div style={{ background:"#fff", borderBottom:`1px solid ${C.border}`,
      padding:"8px 20px", display:"flex", alignItems:"center", gap:10,
      boxShadow:"0 2px 8px rgba(0,0,0,0.06)", marginBottom:12 }}>

      <label style={{ display:"flex", alignItems:"center", gap:6, cursor:"pointer",
        fontSize:13, fontWeight:600, color:C.text, userSelect:"none" }}>
        <input type="checkbox"
          checked={count > 0 && count === total}
          ref={el => { if(el) el.indeterminate = count > 0 && count < total; }}
          onChange={e => e.target.checked ? onSelectAll() : onClearAll()}
          style={{ width:15, height:15, cursor:"pointer", accentColor:color }} />
        {count === 0
          ? "Select all"
          : count === total
            ? `All ${total} selected`
            : `${count} of ${total} selected`}
      </label>

      <div style={{ flex:1 }} />

      {count > 0 && (
        <>
          <button onClick={() => onAction(2)} disabled={loading}
            style={{ padding:"6px 14px", borderRadius:7, border:"none",
              background:"#22C55E", color:"#fff", fontSize:12, fontWeight:600,
              cursor:loading ? "not-allowed" : "pointer", opacity:loading ? 0.6 : 1 }}>
            ✓ Activate ({count})
          </button>
          <button onClick={() => onAction(1)} disabled={loading}
            style={{ padding:"6px 14px", borderRadius:7, border:"none",
              background:"#F59E0B", color:"#fff", fontSize:12, fontWeight:600,
              cursor:loading ? "not-allowed" : "pointer", opacity:loading ? 0.6 : 1 }}>
            ⊘ Deactivate ({count})
          </button>
          <button onClick={() => onAction(4)} disabled={loading}
            style={{ padding:"6px 14px", borderRadius:7, border:"none",
              background:"#EF4444", color:"#fff", fontSize:12, fontWeight:600,
              cursor:loading ? "not-allowed" : "pointer", opacity:loading ? 0.6 : 1 }}>
            🗑 Delete ({count})
          </button>
          <button onClick={onClearAll} disabled={loading}
            style={{ padding:"6px 10px", borderRadius:7,
              border:`1px solid ${C.border}`, background:"transparent",
              color:C.muted, fontSize:12, cursor:"pointer" }}>
            ✕ Clear
          </button>
        </>
      )}
    </div>
  );
}

// ─── Selectable tile wrapper ──────────────────────────────────────────────────
export function SelectableTile({ doc, selected, onToggle, onOpen, subId, showDocType }) {
  const [hovered, setHovered] = useState(false);
  const isSelected = selected.includes(doc.DOCUMENTID);

  return (
    <div style={{ position:"relative" }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}>

      {/* checkbox — shows on hover or when selected */}
      {(hovered || isSelected) && (
        <div onClick={e => { e.stopPropagation(); onToggle(doc.DOCUMENTID); }}
          style={{ position:"absolute", top:8, left:8, zIndex:10,
            width:20, height:20, borderRadius:5,
            background: isSelected ? C.primary : "rgba(255,255,255,0.9)",
            border:`2px solid ${isSelected ? C.primary : C.border}`,
            display:"flex", alignItems:"center", justifyContent:"center",
            cursor:"pointer", boxShadow:"0 1px 4px rgba(0,0,0,0.15)" }}>
          {isSelected && (
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
              <path d="M1.5 5L4 7.5L8.5 2.5" stroke="#fff" strokeWidth="1.8"
                strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )}
        </div>
      )}

      {/* selected border + tint overlay */}
      {isSelected && (
        <div style={{ position:"absolute", inset:0, borderRadius:8, zIndex:5,
          border:`2px solid ${C.primary}`,
          background:"rgba(24,95,165,0.06)", pointerEvents:"none" }} />
      )}

      <GalleryTile doc={doc} showDocType={showDocType}
        onOpen={d => onOpen(d, subId)} />
    </div>
  );
}

// ─── useFilterActions hook ────────────────────────────────────────────────────
export function useFilterActions({ user, subproject, docs, onRefresh }) {
  const [selected,   setSelected]   = useState([]);
  const [actLoading, setActLoading] = useState(false);
  const [actMsg,     setActMsg]     = useState(null);
  const [confirm,    setConfirm]    = useState(null); // { stateid, count }

  const toggleSelect  = (id) =>
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const selectAll    = () => setSelected(docs.map(d => d.DOCUMENTID));
  const clearAll     = () => setSelected([]);
  const clearOnReset = () => setSelected([]);

  const handleBulkAction = (stateid) => {
    if (!selected.length) return;
    setConfirm({ stateid, count: selected.length });
  };

  const handleConfirmed = async () => {
    if (!confirm) return;
    const { stateid } = confirm;
    const label = stateid===2 ? "Activate" : stateid===1 ? "Deactivate" : "Delete";
    setConfirm(null);
    setActLoading(true); setActMsg(null);
    try {
      const fd = new FormData();
      fd.append("subprojectid", subproject.SUBPROJECTID);
      fd.append("userid",       user.USERID);
      fd.append("stateid",      stateid);
      fd.append("documentid",   selected.join(","));
      const res  = await fetch(ACT_DEACT_API,
        { method:"POST", headers:{ "Auth-Code": user.AUTH_CODE }, body: fd });
      const json = await res.json().catch(() => null);
      if (json?.STATUS === "1" || json?.STATUS === 1) {
        setActMsg({ type:"success",
          text:`${label}d ${selected.length} document${selected.length!==1?"s":""} successfully.` });
        setSelected([]);
        setTimeout(() => { onRefresh(); setActMsg(null); }, 1800);
      } else {
        setActMsg({ type:"error",
          text: json?.MESSAGE || json?.message || `${label} failed.` });
      }
    } catch {
      setActMsg({ type:"error", text:"Network error. Please try again." });
    }
    setActLoading(false);
  };

  const handleCancelled = () => setConfirm(null);

  return {
    selected, actLoading, actMsg, confirm,
    toggleSelect, selectAll, clearAll, clearOnReset,
    handleBulkAction, handleConfirmed, handleCancelled,
  };
}

// ─── Action feedback toast ────────────────────────────────────────────────────
export function ActionMsg({ msg }) {
  if(!msg) return null;
  return (
    <div style={{ margin:"10px 20px 0", padding:"10px 14px", borderRadius:8, fontSize:13,
      background: msg.type === "success" ? "#F0FDF4" : "#FEF2F2",
      color:       msg.type === "success" ? "#16A34A"  : "#DC2626",
      border:`1px solid ${msg.type === "success" ? "#BBF7D0" : "#FECACA"}` }}>
      {msg.text}
    </div>
  );
}